/**
 * Standalone Gate Page
 * Gate page for standalone quests, reusing existing gate components
 * but calling standalone endpoints instead of course-bound ones.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTokens } from '../../contexts/TokenContext';
import {
  GateType,
  GateEvaluationResult,
  TranslationQuestion,
  CombineQuestion,
  ExpansionQuestion,
  EssayQuestion,
  GATE_CONFIGS,
} from '../../types/writing';
import { Note } from '../../types/learning';
import {
  getStandaloneGateQuestions,
  submitStandaloneGateAnswer,
  completeStandaloneGate,
} from '../../services/writingService';
import { learningService } from '../../services/learningService';
import TranslationGate from '../../components/writing/TranslationGate';
import CombineGate from '../../components/writing/CombineGate';
import ExpansionGate from '../../components/writing/ExpansionGate';
import EssayGate from '../../components/writing/EssayGate';
import NoteEditor from '../../components/learning/NoteEditor';
import '../learning/SentenceNav.css';
import './GatePage.css';

const StandaloneGatePage: React.FC = () => {
  const { gateType } = useParams<{ gateType: GateType }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['writing', 'common']);
  const { refreshStats } = useTokens();

  const [questions, setQuestions] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentResult, setCurrentResult] = useState<GateEvaluationResult | null>(null);
  const [scores, setScores] = useState<number[]>([]);
  const scoresRef = React.useRef<number[]>([]);
  const [gateComplete, setGateComplete] = useState(false);
  const [lastSubmittedAnswer, setLastSubmittedAnswer] = useState('');
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [prefilledNote, setPrefilledNote] = useState<{ content: string; tags: string[] } | null>(null);

  const config = GATE_CONFIGS.find(g => g.type === gateType);

  // Load questions
  const loadQuestions = useCallback(async () => {
    if (!gateType) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getStandaloneGateQuestions(gateType);
      setQuestions(data.questions);
    } catch (err: any) {
      setError(err.message || t('writing:gatePageError'));
    } finally {
      setLoading(false);
    }
  }, [gateType, t]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  // Submit answer
  const handleSubmit = async (answer: string) => {
    if (!gateType || !questions[currentIndex]) return;

    setSubmitting(true);
    setCurrentResult(null);
    setLastSubmittedAnswer(answer);

    try {
      const result = await submitStandaloneGateAnswer(
        gateType,
        questions[currentIndex].id,
        answer
      );
      setCurrentResult(result);
      const newScores = [...scoresRef.current, result.score];
      scoresRef.current = newScores;
      setScores(newScores);
    } catch (err: any) {
      setError(err.message || t('common:failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Move to next question or complete gate
  const handleNext = async () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentResult(null);
    } else {
      const currentScores = scoresRef.current;
      const finalScore = currentScores.length > 0
        ? Math.round(currentScores.reduce((a, b) => a + b, 0) / currentScores.length)
        : 0;

      try {
        if (gateType) {
          await completeStandaloneGate(gateType, finalScore);
          await refreshStats();
          setGateComplete(true);
        }
      } catch (err: any) {
        setError(err.message || t('common:failed'));
      }
    }
  };

  // Navigate back to standalone writing
  const handleBackToQuest = () => {
    navigate('/writing');
  };

  // Save a single feedback item to notes
  const handleSaveItemToNotes = useCallback((item: string) => {
    if (!gateType) return;
    setPrefilledNote({
      content: item,
      tags: ['writing', gateType],
    });
    setShowNoteEditor(true);
  }, [gateType]);

  // Save all feedback to notes
  const handleSaveToNotes = useCallback(() => {
    if (!currentResult || !gateType) return;

    const gateLabel = config?.nameChinese || gateType;
    const lines: string[] = [];

    lines.push(`[${gateLabel}] Score: ${currentResult.score}%`);
    lines.push('');
    lines.push('--- My Answer ---');
    lines.push(lastSubmittedAnswer);
    lines.push('');
    lines.push('--- Feedback ---');
    lines.push(currentResult.feedback);

    if (currentResult.grammarIssues && currentResult.grammarIssues.length > 0) {
      lines.push('');
      lines.push('--- Grammar Issues ---');
      currentResult.grammarIssues.forEach((issue, i) => {
        lines.push(`${i + 1}. ${issue}`);
      });
    }

    if (currentResult.suggestions && currentResult.suggestions.length > 0) {
      lines.push('');
      lines.push('--- Suggestions ---');
      currentResult.suggestions.forEach((s, i) => {
        lines.push(`${i + 1}. ${s}`);
      });
    }

    if (currentResult.improvedVersion) {
      lines.push('');
      lines.push('--- Improved Version ---');
      lines.push(currentResult.improvedVersion);
    }

    setPrefilledNote({
      content: lines.join('\n'),
      tags: ['writing', gateType],
    });
    setShowNoteEditor(true);
  }, [currentResult, gateType, lastSubmittedAnswer, config]);

  // Save note
  const handleNoteSave = async (noteData: Partial<Note>) => {
    try {
      await learningService.createNote({
        content: noteData.content || '',
        tags: noteData.tags,
        relatedWord: noteData.relatedWord || undefined,
      });
      setShowNoteEditor(false);
      setPrefilledNote(null);
    } catch (err: any) {
      console.error('Failed to save note:', err);
    }
  };

  // Render the appropriate gate component
  const renderGateContent = () => {
    const currentQuestion = questions[currentIndex];
    if (!currentQuestion) return null;

    const commonProps = {
      question: currentQuestion,
      onSubmit: handleSubmit,
      submitting,
      result: currentResult,
      onSaveToNotes: currentResult ? handleSaveToNotes : undefined,
      onSaveItemToNotes: currentResult ? handleSaveItemToNotes : undefined,
    };

    switch (gateType) {
      case 'translation':
        return <TranslationGate {...commonProps} question={currentQuestion as TranslationQuestion} />;
      case 'combine':
        return <CombineGate {...commonProps} question={currentQuestion as CombineQuestion} />;
      case 'expansion':
        return <ExpansionGate {...commonProps} question={currentQuestion as ExpansionQuestion} />;
      case 'essay':
        return <EssayGate {...commonProps} question={currentQuestion as EssayQuestion} />;
      default:
        return <div>{t('writing:unknownGateType')}</div>;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="gate-page gate-page--loading">
        <div className="gate-loading">
          <div className="gate-loading__spinner" />
          <p>{t('loadingQuestions')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="gate-page gate-page--error">
        <div className="gate-error">
          <h3>{t('error', { ns: 'common' })}</h3>
          <p>{error}</p>
          <button onClick={loadQuestions} className="gate-error__retry">
            {t('retry', { ns: 'common' })}
          </button>
          <button onClick={handleBackToQuest} className="gate-error__back">
            {t('backToQuest')}
          </button>
        </div>
      </div>
    );
  }

  // Gate complete state
  if (gateComplete) {
    const currentScores = scoresRef.current;
    const finalScore = currentScores.length > 0
      ? Math.round(currentScores.reduce((a, b) => a + b, 0) / currentScores.length)
      : 0;
    const passed = finalScore >= 60;

    return (
      <div className="gate-page gate-page--complete">
        <div className="gate-complete">
          <div className="gate-complete__icon">
            {passed ? (
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 010-5H6"/><path d="M18 9h1.5a2.5 2.5 0 000-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22"/><path d="M18 2H6v7a6 6 0 1012 0V2z"/></svg>
            ) : (
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
            )}
          </div>
          <h2 className="gate-complete__title">
            {passed ? t('gatePassed') : t('gateFailed')}
          </h2>
          <div className="gate-complete__score">
            <span className={`gate-complete__score-value ${passed ? 'pass' : 'fail'}`}>
              {finalScore}%
            </span>
          </div>
          {passed && (
            <div className="gate-complete__rewards">
              <span className="gate-complete__xp">+{config?.xpReward || 0} XP</span>
            </div>
          )}
          <p className="gate-complete__message">
            {passed ? t('gatePassedMessage') : t('gateFailedMessage')}
          </p>
          <button className="gate-complete__btn" onClick={handleBackToQuest}>
            {t('continueJourney')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="gate-page">
      {/* Header */}
      <div className="learning-mode-header">
        <button className="quest-back-btn" onClick={handleBackToQuest}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
        </button>
        <h2 className="learning-mode-header__title">
          <span className="gate-title-icon">{config?.icon}</span>
          {config?.nameChinese || gateType}
        </h2>
        <div className="learning-mode-header__nav">
          <button className="sentence-nav-btn" disabled={true}>◀</button>
          <span className="sentence-nav-counter">{currentIndex + 1} / {questions.length}</span>
          <button className="sentence-nav-btn" disabled={true}>▶</button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="gate-progress-bar">
        <div
          className="gate-progress-bar__fill"
          style={{ width: `${((currentIndex + (currentResult ? 1 : 0)) / questions.length) * 100}%` }}
        />
      </div>

      {/* Main content */}
      <section className="gate-content">
        {renderGateContent()}
      </section>

      {/* Footer with next button (shown after result) */}
      {currentResult && (
        <footer className="gate-footer">
          <div className="gate-result-summary">
            <span className={`gate-result-badge ${currentResult.passed ? 'pass' : 'fail'}`}>
              {currentResult.passed ? t('correct') : t('needsWork')}
            </span>
            <span className="gate-result-score">
              {currentResult.score}%
            </span>
          </div>
          <button className="gate-next-btn" onClick={handleNext}>
            {currentIndex < questions.length - 1 ? t('nextQuestion') : t('finishGate')}
          </button>
        </footer>
      )}

      {/* NoteEditor Modal */}
      {showNoteEditor && (
        <NoteEditor
          note={null}
          onSave={handleNoteSave}
          onClose={() => { setShowNoteEditor(false); setPrefilledNote(null); }}
          prefilledContent={prefilledNote}
        />
      )}
    </div>
  );
};

export default StandaloneGatePage;
